import type { ComponentType } from 'react';
import { AgentPanel } from '../panels/AgentPanel';
import { AgentManagerPanel } from '../panels/AgentManagerPanel';
import { AppPanel } from '../panels/AppPanel';
import { AppsPanel } from '../panels/AppsPanel';
import { ChatPanel } from '../panels/ChatPanel';
import { FileExplorerPanel } from '../panels/FileExplorerPanel';
import { FilePanel } from '../panels/FilePanel';
import { LivePanel } from '../panels/LivePanel';
import { MessageDetailPanel } from '../panels/MessageDetailPanel';
import { MessagesPanel } from '../panels/MessagesPanel';
import { PromptPanel } from '../panels/PromptPanel';
import { PromptComposerPanel } from '../panels/PromptComposerPanel';
import { SessionsPanel } from '../panels/SessionsPanel';
import { VoicePromptPanel } from '../panels/VoicePromptPanel';
import { VersionsPanel } from '../panels/VersionsPanel';
import { DiffPanel } from '../panels/DiffPanel';
import { TasksPanel } from '../panels/TasksPanel';
import { AssignmentsPanel } from '../panels/AssignmentsPanel';

export type PanelComponentId =
  | 'agent'
  | 'agentManager'
  | 'app'
  | 'apps'
  | 'chat'
  | 'file'
  | 'fileExplorer'
  | 'live'
  | 'diff'
  | 'tasks'
  | 'assignments'
  | 'messageDetail'
  | 'messages'
  | 'prompt'
  | 'promptComposer'
  | 'sessions'
  | 'voicePrompt'
  | 'versions';

export interface PanelDefinition {
  id: PanelComponentId;
  label: string;
  description: string;
  component: ComponentType;
}

export const PANEL_DEFINITIONS: PanelDefinition[] = [
  {
    id: 'agent',
    label: 'Agent',
    description: 'Runtime controls and status indicators for the agent loop',
    component: AgentPanel,
  },
  {
    id: 'agentManager',
    label: 'Agent Manager',
    description: 'Create, organize, and configure agents',
    component: AgentManagerPanel,
  },
  {
    id: 'app',
    label: 'App',
    description: 'Application preview iframe',
    component: AppPanel,
  },
  {
    id: 'apps',
    label: 'Apps',
    description: 'Create, update, and manage hosted apps',
    component: AppsPanel,
  },
  {
    id: 'chat',
    label: 'Chat',
    description: 'Conversation history with user and assistant messages',
    component: ChatPanel,
  },
  {
    id: 'file',
    label: 'File viewer',
    description: 'View and syntax highlight file contents',
    component: FilePanel,
  },
  {
    id: 'fileExplorer',
    label: 'File explorer',
    description: 'Artifacts and files synced from the workspace',
    component: FileExplorerPanel,
  },
  {
    id: 'diff',
    label: 'Diff',
    description: 'Compare file changes across versions',
    component: DiffPanel,
  },
  {
    id: 'tasks',
    label: 'Tasks',
    description: 'Author reusable tasks and drag them onto agents',
    component: TasksPanel,
  },
  {
    id: 'assignments',
    label: 'Assignments',
    description: 'Queue tasks for agents and review their state',
    component: AssignmentsPanel,
  },
  {
    id: 'live',
    label: 'Live',
    description: 'Real-time event stream from the bus',
    component: LivePanel,
  },
  {
    id: 'messageDetail',
    label: 'Message detail',
    description: 'Inspect message blocks, tool payloads, and revisions',
    component: MessageDetailPanel,
  },
  {
    id: 'messages',
    label: 'Messages',
    description: 'Messages within the selected session',
    component: MessagesPanel,
  },
  {
    id: 'prompt',
    label: 'Prompt',
    description: 'Send messages and prompts to the agent',
    component: PromptPanel,
  },
  {
    id: 'promptComposer',
    label: 'Prompt Composer',
    description: 'Compose prompts by combining saved fragments',
    component: PromptComposerPanel,
  },
  {
    id: 'sessions',
    label: 'Sessions',
    description: 'List of conversation sessions',
    component: SessionsPanel,
  },
  {
    id: 'voicePrompt',
    label: 'Voice Prompt',
    description: 'Capture audio, visualize levels, and send transcription to the agent',
    component: VoicePromptPanel,
  },
  {
    id: 'versions',
    label: 'Versions',
    description: 'File version history and checkout',
    component: VersionsPanel,
  },
];

export const PANEL_DEFINITION_MAP: Record<PanelComponentId, PanelDefinition> = PANEL_DEFINITIONS.reduce(
  (acc, definition) => {
    acc[definition.id] = definition;
    return acc;
  },
  {} as Record<PanelComponentId, PanelDefinition>,
);

export const PANEL_MENU_OPTIONS = PANEL_DEFINITIONS.map((definition) => ({
  id: definition.id,
  label: definition.label,
  description: definition.description,
}));
